import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { Download, ArrowDownRight } from 'lucide-react';
import { Link } from 'react-scroll';
import { useLang } from '../i18n/LanguageContext';

const Hero = memo(() => {
  const { t } = useLang();
  const meta = t.hero.meta;

  const downloadCV = () => {
    const link = document.createElement('a');
    link.href = process.env.PUBLIC_URL + '/YigitcanAvaz-CV.pdf';
    link.download = 'YigitcanAvaz-CV.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <section id="home" className="relative pt-10 md:pt-16">
      <div className="container-ed">
        {/* top meta row */}
        <div className="flex items-center justify-between label text-ink/60 pb-6">
          <span>{t.hero.portfolio} — {new Date().getFullYear()}</span>
          <span className="hidden sm:inline">{t.hero.eduline}</span>
          <span>İST · GOUDA</span>
        </div>

        {/* Masthead name */}
        <div className="rule pt-4">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="display text-[13vw] sm:text-[11vw] lg:text-[8.5rem] leading-[0.85]"
          >
            YİĞİTCAN
            <br />
            <span className="text-outline">AVAZ.</span>
          </motion.h1>
        </div>

        {/* intro paragraph + CTA, asymmetric */}
        <div className="grid lg:grid-cols-12 gap-8 mt-10 border-t border-ink/20 pt-8">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="lg:col-span-5 lg:col-start-1"
          >
            <div className="flex items-start gap-2 text-xl md:text-2xl font-semibold leading-snug tracking-tight">
              <ArrowDownRight className="w-6 h-6 mt-0.5 shrink-0 text-accent" />
              <p>
                {t.hero.lead.pre}
                <span className="text-accent">{t.hero.lead.accent}</span>
                {t.hero.lead.post}
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="lg:col-span-5 lg:col-start-8"
          >
            <p className="text-ink/70 leading-relaxed text-sm md:text-base">
              {t.hero.paragraph}
            </p>
            <div className="flex flex-wrap gap-3 mt-7">
              <button onClick={downloadCV} className="btn-solid">
                <Download size={16} />
                {t.hero.downloadCV}
              </button>
              <Link to="projects" smooth offset={-64} duration={500} className="btn-line cursor-pointer">
                {t.hero.viewProjects}
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Meta cells */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.4 }}
          className="grid grid-cols-2 lg:grid-cols-4 border-t border-ink/20 mt-12"
        >
          {meta.map((m, i) => (
            <div
              key={m.k}
              className={`py-6 lg:py-8 ${i !== 0 ? 'lg:border-l border-ink/20 lg:pl-6' : ''} ${i % 2 !== 0 ? 'border-l border-ink/20 pl-6 lg:pl-6' : ''} ${i >= 2 ? 'border-t lg:border-t-0 border-ink/20' : ''}`}
            >
              <div className="label text-ink/45 mb-2">{m.k}</div>
              <div className="text-sm md:text-base font-semibold leading-snug">{m.v}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
});

Hero.displayName = 'Hero';

export default Hero;
